// packages/frontend/src/components/DealsList/DealStageActions/SubmitCTVO.tsx
import { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';

import { toFrPhoneInputValue, toStorage } from '../../../utils/frPhone';

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SaveIcon from '@mui/icons-material/Save';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';

import { makeAuthenticatedApiCall } from '../../../apiHelper';
import { pieceDetachmentEndpoints, requestPieceDetachment } from '../../../utils/pieceDetachment';
import { stripDealBlobs } from '../../../utils/stripDealBlobs';
import { useDealMisivF } from '../../../hooks/useApiQueries';
import usePreDialogValidation from '../../../hooks/usePreDialogValidation';
import theme from '../../../theme';
import { formatUTCDateTime, getFormatFromMimetype, removeSpaces } from '../../../utils';
import { getCommonValidationRules } from '../../../utils/formValidationRules';
import { normalizeTypeVoie } from '../../../utils/normalizeTypeVoie';
import { ValidationField } from '../../../utils/validationUtils';
import FilesOpener from '../../CGForm/FilesOpener';
import useButtonStepStore from '../../CGList/ButtonStepStore';
import FirebaseUploadComponent from '../../CreateCotation/FirebaseUploadComponent';
import CfnPiecesWarning from './CfnPiecesWarning';
import { ErrorMessageSnackbar } from '../../ErrorMessageSnackbar';
import { companyBlockDefault, genderBlockDefault, personBlockDefault } from './entityBlockDefaults';

interface SubmitCTVO {
  deal: any;
  pipelineLetter: string;
  stageId: number;
  userRole?: string;
  onRefresh?: (changeCurrentTab?: number) => void;
  /**
   * Hook optionnel exécuté AVANT l'ouverture de la modale de soumission. Utilisé
   * par /review pour flusher les corrections du god (auto-save par champ) puis
   * recharger le dossier, afin que la modale — et donc MiSIV — parte des valeurs
   * corrigées et non de l'état chargé au montage de la page. Non fourni depuis
   * list-cg (comportement inchangé).
   */
  onBeforeOpen?: () => Promise<void>;
}

const SubmitCTVO: React.FC<SubmitCTVO> = ({ deal, pipelineLetter, stageId, userRole, onRefresh, onBeforeOpen }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [isSavingData, setIsSavingData] = useState(false);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [buttonText, setButtonText] = useState<string>('');
  const [responseNomFic, setResponseNomFic] = useState<string>('');
  const [responseId, setResponseId] = useState<number>();
  const [errorText, setErrorText] = useState<string>('');
  const [isTimeoutError, setIsTimeoutError] = useState(false);
  const [hasFirebaseUrlOnly, setHasFirebaseUrlOnly] = useState(false);

  const validation = usePreDialogValidation();

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [responseErrorCode, setResponseErrorCode] = useState(null);
  const [purchaseDateTime, setPurchaseDateTime] = useState('');

  const [uploadedUrls, setUploadedUrls] = useState<string[]>([]);

  const [dealDataToSend, setDealDataToSend] = useState<any>(null);

  // États pour l'autocomplete du lieu de naissance
  const [birthDepartmentOptions, setBirthDepartmentOptions] = useState<{ label: string; value: string }[]>([]);
  const [birthPlaceInputValue, setBirthPlaceInputValue] = useState<string>(deal?.BuyerPlaceOfBirth ?? '');

  // États pour la modale de soumission manuelle du CTVO
  const [isManualCTVOModalOpen, setIsManualCTVOModalOpen] = useState(false);
  const [manualReceiptFileName, setManualReceiptFileName] = useState('');
  const [manualReceiptFirebaseUrl, setManualReceiptFirebaseUrl] = useState('');
  const [manualCTVOMethod, setManualCTVOMethod] = useState<'filename' | 'url'>('filename');
  const [shouldInvoiceCTVO, setShouldInvoiceCTVO] = useState(true);
  const [isSubmittingManual, setIsSubmittingManual] = useState(false);

  const env = window.location.hostname === 'app.skipcar.fr' ? 'production' : 'development';

  const isNumFormuleExists = !deal?.NumFormule;

  const { data: misivDealData } = useDealMisivF(deal.deal_id || deal.PipedriveDealID);

  const { buttonSteps, setButtonStep } = useButtonStepStore();

  const queryClient = useQueryClient();

  const formMethods = useForm({
    mode: 'all', // Validation à chaque changement de champ
  });

  const handleDownloadPhotos = async () => {
    setDownloading(true);
    const zip = new JSZip();
    const folder = zip.folder(`Skipcar-CTVO-${deal.deal_id || deal.PipedriveDealID}`);

    // Parse the deal.AllFiles string into an array of URLs
    const urls = deal.AllFiles.split(',').map((url: string) => url.trim());

    const promises = urls.map(async (url: string, index: number) => {
      try {
        const response = await axios.get(url, { responseType: 'arraybuffer' });
        const blob = new Blob([response.data], { type: String(response.headers['content-type'] ?? '') });
        const fileName = `Skipcar-CTVO-${deal.deal_id || deal.PipedriveDealID}-${index}.${getFormatFromMimetype(String(response.headers['content-type'] ?? ''))}`;
        folder?.file(fileName, blob);
      } catch (error) {
        console.error('Error handling photo:', error);
      }
    });

    await Promise.all(promises);

    try {
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `Skipcar-CTVO-${deal.deal_id || deal.PipedriveDealID}.zip`);
    } catch (error) {
      console.error('Error generating ZIP:', error);
    } finally {
      setDownloading(false);
    }
  };

  const { handleSubmit, control, setValue, watch, formState, trigger } = formMethods;
  const { errors, isValid } = formState;

  const validationRules = getCommonValidationRules(formMethods);

  const openSnackbar = validation.openSnackbar;

  // Les pièces listées ici partent au coffre-fort numérique : un dépôt est
  // rattaché au dossier (`otherFiles`) et un retrait enlève la référence en
  // base AVANT la suppression Storage, sinon le worker CFN tombe sur une URL
  // morte. `RegistrationID` n'existe qu'en pipeline F (list-cg / review).
  const registrationId: number | undefined = deal?.RegistrationID;

  const handleUploadComplete = async (url: string) => {
    setUploadedUrls((prevUrls) => [...prevUrls, url]);
    if (!registrationId) return;
    try {
      await makeAuthenticatedApiCall('post', pieceDetachmentEndpoints.cg(registrationId), { url });
    } catch (error: any) {
      console.error('SubmitCTVO / rattachement de la pièce au dossier :', JSON.stringify(error));
      openSnackbar(
        error?.response?.data?.error || 'Pièce déposée mais non rattachée au dossier : elle ne partira pas au coffre.',
        'error',
      );
    }
  };

  const handleBeforeDelete = async (url: string) => {
    if (!registrationId) return;
    return requestPieceDetachment(pieceDetachmentEndpoints.cg(registrationId), url);
  };

  const handleDeleteComplete = (url: string) => {
    setUploadedUrls((prevUrls) => prevUrls.filter((existingUrl) => existingUrl !== url));
    onRefresh?.();
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  const BuyerCountryOfBirth = watch('BuyerCountryOfBirth');
  const CoBuyer = watch('CoBuyer');
  const CoBuyerType = watch('CoBuyerType');
  const SellerType = watch('SellerType');
  const BuyerType = watch('BuyerType');
  const _BuyerResidenceType = watch('BuyerResidenceType'); // Pour usage futur (validations conditionnelles)

  const isFrance = BuyerCountryOfBirth === 'FRANCE';

  // Fonction pour rechercher les villes de naissance (autocomplete)
  const handleBirthCityChange = async (_event: any, value: string) => {
    if (value && value.length > 3) {
      try {
        const response = await makeAuthenticatedApiCall(
          'get',
          `/api/geo/cities/?searchParam=${value}&searchType=municipality`,
        );
        const responseData = response.data as { name: string; postcode: string }[] | null;
        if (responseData && responseData.length > 0) {
          const options = responseData.map((item) => ({
            label: item.name,
            value: item.postcode,
          }));
          setBirthDepartmentOptions(options);
        } else {
          setBirthDepartmentOptions([]);
        }
      } catch (error) {
        console.error('Error fetching city data:', error);
        setBirthDepartmentOptions([]);
      }
    } else {
      setBirthDepartmentOptions([]);
    }
  };

  useEffect(() => {
    if (BuyerCountryOfBirth === 'FRANCE') {
      trigger('buyerDepartmentOfBirth'); // Déclenchez la revalidation du champ si le pays est la France
    }
  }, [BuyerCountryOfBirth, trigger]);

  useEffect(() => {
    if (CoBuyer === 'yes') {
      trigger('CoBuyerType');
    }
  }, [CoBuyerType, CoBuyer, trigger]);

  useEffect(() => {
    const determineButtonState = () => {
      if (stageId === 46) {
        // F1
        return 'waiting-payment';
      }

      const data = misivDealData?.data.getDealMisivFromDB
        .filter((data: any) => data.receipt_file_name?.startsWith('CTVO-') || data.receipt_file_firebase_url)
        .sort((a: any, b: any) => b.id - a.id || b.response_id - a.response_id)[0];

      if (!data) {
        if (userRole === 'agency' && stageId === 48) {
          // F3
          return 'waiting-procedures';
        } else if (stageId === 47 && userRole === 'agency') {
          // F2
          return 'waiting-procedures';
        } else {
          return 'initial';
        }
      }

      const { receipt_file_firebase_url, receipt_file_name, response_id, has_timeout, purchase_date_time } = data;

      if (
        ((receipt_file_name && receipt_file_name.startsWith('CTVO-')) ||
          (receipt_file_firebase_url && receipt_file_firebase_url.includes('/CTVO/'))) &&
        response_id &&
        has_timeout === 0
      ) {
        // Cas spécial : URL Firebase sans nom de fichier
        const hasFirebaseUrlOnly = receipt_file_firebase_url && !receipt_file_name;
        setHasFirebaseUrlOnly(hasFirebaseUrlOnly);
        setResponseNomFic(receipt_file_name);
        if (userRole === 'agency') {
          setButtonText(`✅ Télécharger récépissé CTVO `);
        } else {
          setButtonText(`✅ CTVO n°${response_id} ${userRole === 'god' && receipt_file_firebase_url ? '' : 'KO'}`);
        }
        setIsTimeoutError(false);

        if (userRole === 'agency' && stageId === 48) {
          // F3
          return 'receipt-only';
        } else {
          return 'receipt';
        }
      } else if (!receipt_file_name && has_timeout === 1) {
        setResponseId(response_id);
        setIsTimeoutError(true);
        setButtonText(`Renvoyer au ministère n°${response_id}`);
        return 'timeout-error';
      } else if (!receipt_file_name && has_timeout === 0) {
        setPurchaseDateTime(formatUTCDateTime(purchase_date_time));
        setIsTimeoutError(false);
        setButtonText(`⚠️ CTVO n°${response_id}`);
        return 'save';
      }

      return 'initial';
    };

    const newState = determineButtonState();
    setButtonStep(deal.deal_id || deal.PipedriveDealID, newState);
  }, [misivDealData, stageId]);

  useEffect(() => {
    if (deal?.AllFiles) {
      const files = [...(pipelineLetter === 'f' && deal?.AllFiles ? deal?.AllFiles.split(',') : [])];

      setUploadedUrls(files);
    }
  }, [deal, pipelineLetter]);

  useEffect(() => {
    if (deal?.PurchaseDateTime) {
      const formattedDate = formatUTCDateTime(deal.PurchaseDateTime);
      setPurchaseDateTime(formattedDate);
    }
  }, [deal]);

  const handleConfirmClick = () => {
    setIsConfirmModalOpen(true); // Ouvrir la modale de confirmation au lieu de soumettre directement
  };

  // Quand CoBuyer=no, les champs CoBuyer* ne doivent pas partir au backend :
  // un CoBuyerType résiduel (défaut UI ou bascule Oui→Non) faisait planter
  // saveCTVO sur .trim() d'un nom null (deal 42937, 2026-09-17).
  const stripCoBuyerFieldsIfDeclined = <T extends Record<string, unknown>>(formData: T): T => {
    if (formData.CoBuyer !== 'no') {
      return formData;
    }

    const {
      CoBuyerType: _coBuyerType,
      CoBuyerLastName: _coBuyerLastName,
      CoBuyerFirstName: _coBuyerFirstName,
      CoBuyerUsualName: _coBuyerUsualName,
      CoBuyerGender: _coBuyerGender,
      CoBuyerCompanyName: _coBuyerCompanyName,
      CoBuyerSIRET: _coBuyerSIRET,
      ...rest
    } = formData;

    return rest as T;
  };

  /** Widget téléphone → format canonique 0XXXXXXXXX avant API / MiSIV. */
  const withCanonicalBuyerPhone = <T extends Record<string, unknown>>(data: T): T => ({
    ...data,
    ...(Object.prototype.hasOwnProperty.call(data, 'BuyerPhone')
      ? { BuyerPhone: toStorage(data.BuyerPhone) }
      : {}),
  });

  const handleFinalConfirm = async () => {
    const formData = formMethods.getValues();
    // stripDealBlobs : cet objet part tel quel dans le body de /api/misiv/save|submit/ctvo,
    // dont le parser global plafonne à 100 kb. Le deal issu de /review porte
    // IngestionTrace (~489 Ko) → 413. No-op sur le deal issu de list-cg.
    const updatedDealWithFormDatas = withCanonicalBuyerPhone(
      stripCoBuyerFieldsIfDeclined({
        ...stripDealBlobs(deal),
        ...formData,
      }),
    );
    const formDataForSave = withCanonicalBuyerPhone(stripCoBuyerFieldsIfDeclined(formData));
    setDealDataToSend(updatedDealWithFormDatas);

    setIsConfirmModalOpen(false); // Fermer la modale de confirmation
    const handleSaveResponse = await handleSave(formDataForSave);
    if (handleSaveResponse) {
      await handleSaveCTVO(updatedDealWithFormDatas); // Appeler la fonction pour soumettre
      handleDialogClose(); // Fermer la modale principale
    }
  };

  const handleSave = async (formData: any /* FormDataPerson | FormDataPro */) => {
    try {
      const purchaseDateTime = formMethods.getValues('purchaseDateTime');
      const dealWithPurchaseDateTime = { ...formData, purchaseDateTime, type: 'CTVO' };

      await makeAuthenticatedApiCall('post', `/api/cg/update`, {
        dealDataToSend: dealWithPurchaseDateTime,
        deal_id: deal.deal_id || deal.PipedriveDealID, // Support both for backward compatibility
        // Repli quand le dossier n'a pas d'identifiant interne (soumission
        // agence interrompue) : sans lui, le serveur ne retrouve rien et la
        // modale affichait « Request failed with status code 500 ».
        registrationId: deal.RegistrationID,
      });
      // openSnackbar('Les modifications ont bien été prises en compte', 'success');
      return true;
    } catch (error: any) {
      console.error(error);
      // `message` du serveur AVANT `error.message` d'axios : sinon le relecteur
      // lit « Request failed with status code 400/500 » à la place de
      // l'explication rédigée pour lui.
      openSnackbar(
        error?.response?.data?.message || error?.response?.data?.error || error.message,
        'error',
      );
      return false;
    } finally {
      setResponseErrorCode(null);
    }
  };

  // Fonction pour enregistrer les données sans soumettre à MISIV
  const handleSaveData = async () => {
    setIsSavingData(true);
    try {
      // Récupérer toutes les données du formulaire
      const formData = withCanonicalBuyerPhone(stripCoBuyerFieldsIfDeclined(formMethods.getValues()));
      const purchaseDateTime = formMethods.getValues('purchaseDateTime');

      // Appeler handleSave pour enregistrer dans la base de données
      const success = await handleSave(formData);

      if (success) {
        // Invalider et refetch les queries pour actualiser les données
        await queryClient.invalidateQueries({ queryKey: ['useArbitratorDeals'] });
        await queryClient.refetchQueries({ queryKey: ['useArbitratorDeals'] });

        openSnackbar('Données enregistrées avec succès', 'success');
      }
    } catch (error: any) {
      console.error("Erreur lors de l'enregistrement:", error);
      openSnackbar(error?.message || "Erreur lors de l'enregistrement des données", 'error');
    } finally {
      setIsSavingData(false);
    }
  };

  const handleSaveCTVO = async (dealDataToSend: any) => {
    setIsLoading(true);
    validation.setDisabledButton(true);

    try {
      const responseSave = await makeAuthenticatedApiCall('post', `/api/misiv/save/ctvo`, dealDataToSend);
      const responseSubmitId = responseSave?.data.responseId;
      setButtonStep(deal.deal_id || deal.PipedriveDealID, 'save');
      setButtonText(`⚠️ CTVO n°${responseSubmitId}`);
      await handleSubmitCTVO(dealDataToSend);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la demande:', error);
      openSnackbar('Erreur lors de la sauvegarde de la demande. Veuillez ré-essayer.', 'error');
      setIsLoading(false);
      validation.setDisabledButton(false);
    }
  };
  const handleSubmitCTVO = async (dealDataToSend: any) => {
    setIsLoading(true);
    validation.setDisabledButton(true);

    try {
      const responseSubmit = await makeAuthenticatedApiCall('post', `/api/misiv/submit/ctvo`, dealDataToSend);

      const responseId = responseSubmit.data.responseId;
      const responseNomFic = responseSubmit.data.responseNomFic;
      setResponseNomFic(responseNomFic);

      // Gérer le cas où il n'y a pas d'erreur
      openSnackbar(`Votre demande a bien été envoyée : ${responseId}`, 'success');
      setButtonStep(deal.deal_id || deal.PipedriveDealID, 'receipt');
      if (userRole === 'agency') {
        setButtonText(`✅ Télécharger récépissé CTVO `);
      } else {
        if (env === 'production') {
          // Ici route pour envoyer le mail de confirmation
          // L'email de l'agence est récupéré côté backend via le dealId
          const dealIdToSend = deal.deal_id || deal.PipedriveDealID;
          console.log(
            '📧 Sending CTVO email - dealId:',
            dealIdToSend,
            'deal.deal_id:',
            deal.deal_id,
            'deal.PipedriveDealID:',
            deal.PipedriveDealID,
          );

          if (!dealIdToSend) {
            console.error('❌ Cannot send CTVO email: dealId is undefined/null', { deal });
            openSnackbar("Erreur: Impossible d'envoyer l'email (deal_id manquant)", 'error');
          } else {
            const responseSendEmailToAgency = await makeAuthenticatedApiCall('post', `/api/mails/ctvo/agency`, {
              dealId: dealIdToSend,
              RegPlate: dealDataToSend.RegPlate,
            });
            console.log('responseSendEmailToAgency', responseSendEmailToAgency);
          }
        }
        setButtonText(`✅ CTVO n°${responseId}`);
      }
      /*
      const base64String = responseSubmit.data.askCessionReceipt;
      const blob = base64ToBlob(base64String, 'application/pdf');
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl);
      */
      // BUG 2 (retour François 2026-07) : parfois le récépissé ne s'ouvre pas et
      // l'user reste bloqué. On sécurise l'accès (optional chaining) pour éviter
      // un TypeError si firebaseReceipt est absent d'une réponse 200, et on trace
      // ce cas pour confirmer sa fréquence côté Sentry.
      const firebaseUrl = responseSubmit.data?.firebaseReceipt?.firebaseReceiptUrl;
      if (firebaseUrl) {
        // Si nous recevons une URL Firebase, ouvrez-la directement
        window.open(firebaseUrl);
        // 🎯 NOUVEAU : La transition vers le stage 48 se fait maintenant automatiquement côté backend
        // lors de la création du receipt Firebase. Plus besoin de l'appel manuel.
        if (onRefresh) {
          onRefresh(); // Rafraîchir pour voir la mise à jour automatique
        }
      } else {
        Sentry.captureMessage('SubmitCTVO: réponse 200 sans firebaseReceiptUrl (récépissé non ouvert)', {
          level: 'error',
          tags: { flow: 'ctvo' },
          extra: {
            dealId: deal.deal_id || deal.PipedriveDealID,
            RegPlate: dealDataToSend?.RegPlate,
            responseId,
            hasFirebaseReceipt: Boolean(responseSubmit.data?.firebaseReceipt),
          },
        });
      }
    } catch (error: any) {
      // Un refus métier du SIV n'est pas une panne : le ministère a répondu, l'opérateur
      // voit le code et le motif dans le snackbar plus bas, et le backend trace désormais
      // le refus avec deal, plaque et numéro de formule. Le remonter ici en plus le
      // déguisait en « AxiosError 500 » sans motif, une issue Sentry sur laquelle il n'y
      // a rien à faire et qui noie les vraies (cf. FRONTEND-22B, refus code 14 du 16/09).
      const misivBusinessRejection =
        error?.response?.status === 500 && error?.response?.data?.responseErrorCode !== undefined;

      // BUG 2 (retour François 2026-07) : tracer précisément le mode d'échec
      // (524 timeout Cloudflare / 500 MISIV / TypeError récépissé absent) qui
      // laisse l'user bloqué sans récépissé. Le statut HTTP + la plaque
      // permettent de recouper avec les logs backend.
      if (!misivBusinessRejection) {
        Sentry.captureException(error, {
          tags: { flow: 'ctvo' },
          extra: {
            httpStatus: error?.response?.status,
            dealId: deal.deal_id || deal.PipedriveDealID,
            RegPlate: dealDataToSend?.RegPlate,
            hasResponse: Boolean(error?.response),
            responseDataKeys: error?.response?.data ? Object.keys(error.response.data) : null,
          },
        });
      }
      if (error.response && error.response.status === 400) {
        // Cas où le serveur a renvoyé une erreur de validation (400)
        const validationMessage = error.response.data?.message;
        if (validationMessage) {
          openSnackbar(validationMessage, 'error');
          setErrorText(validationMessage);
        } else {
          openSnackbar('Erreur de validation des données', 'error');
        }
        validation.setDisabledButton(false);
        return;
      } else if (error.response && error.response.status === 500) {
        // Cas où le serveur a renvoyé une erreur 500 avec des données JSON
        const responseId = error.response.data?.responseId;
        const responseErrorCode = error.response.data?.responseErrorCode;
        const responseErrorMessage = error.response.data?.responseErrorMessage;

        if (responseErrorCode === 3 || responseErrorCode === 100) {
          // Ici on doit pouvoir re-cliquer et re-tenter l'envoi sans passer par la modale avec le form
          setResponseId(responseId);
          setIsTimeoutError(true);
          setButtonText(`Renvoyer au ministère n°${responseId}`);
          openSnackbar(`Timeout Ministère : Ré-essayer dans quelques instants`, 'error');
          return;
        } else {
          // Enregistrez le code d'erreur pour une utilisation ultérieure
          setResponseErrorCode(responseErrorCode);
        }

        if (responseErrorCode) {
          openSnackbar(`Erreur ${responseErrorCode} : ${responseErrorMessage}`, 'error');
          setErrorText(responseErrorMessage);
        } else {
          console.log(error);
          openSnackbar(error.response.data.error, 'error');
          setErrorText(error.response.data.error);
        }

        return;
      } else if (axios.isAxiosError(error)) {
        // Autres erreurs Axios avec une réponse du serveur
        if (error.response?.data?.message) {
          openSnackbar(error.response.data.message, 'error');
          setErrorText(error.response.data.message);
        } else {
          console.error('Erreur réseau ou serveur :', error.message);
          openSnackbar('Erreur réseau ou serveur : ' + error.message, 'error');
        }
      } else {
        console.error("Erreur inattendue lors de l'envoi du certificat à TMS :", error);
        openSnackbar(`Erreur lors de l'envoi du certificat à TMS`, 'error');
      }
      validation.setDisabledButton(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendToMinistry = async (responseSubmitId: number) => {
    setIsLoading(true);

    try {
      const responseOpen = await makeAuthenticatedApiCall('post', `/api/misiv/open/ctvo`, {
        responseSubmitId,
      });
      handleGetReceipt(responseOpen.data.jsonObj);
      setButtonStep(deal.deal_id || deal.PipedriveDealID, 'receipt');
      setButtonText(`✅ CTVO n°${responseSubmitId}`);
      validation.setTooltipError('');
      setResponseNomFic(responseOpen.data.jsonObj);
    } catch (error: any) {
      console.error("Erreur lors de l'ouverture du certificat à TMS :", error);
      openSnackbar(error.response.data.error, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGetReceipt = async (responseNomFic: string) => {
    setIsLoading(true);

    try {
      const responseReceipt = await makeAuthenticatedApiCall('post', `/api/misiv/receipt/ctvo`, {
        responseNomFic,
        pipelineLetter,
        dealId: deal.deal_id || deal.PipedriveDealID,
        typeDemarche: 'CTVO',
      });

      const firebaseUrl = responseReceipt.data.firebaseUrl;
      if (firebaseUrl) {
        window.open(firebaseUrl);
        // 🎯 NOUVEAU : La transition vers le stage 48 se fait maintenant automatiquement côté backend
        // Pas besoin d'appel manuel ici car le receipt était déjà créé auparavant
        if (onRefresh) {
          onRefresh(); // Rafraîchir pour voir l'état actuel
        }
      }
      setButtonStep(deal.deal_id || deal.PipedriveDealID, 'receipt');
      setIsTimeoutError(false);
    } catch (error: any) {
      console.error('handleGetReceipt - Erreur lors de la génération du reçu :', error);

      const errorMessage = error.response?.data?.message || 'Erreur inconnue';
      openSnackbar(errorMessage, 'error');
      setErrorText(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAndValidateCTVO = async (): Promise<{
    fields: ValidationField[];
    dealId: string | number;
    context?: string;
  } | null> => {
    const dealId = deal.deal_id || deal.PipedriveDealID;
    const fields: ValidationField[] = [{ key: 'num_formule', value: deal?.NumFormule }];
    return { dealId, context: 'CTVO', fields };
  };

  const handleButtonClick = async () => {
    if (buttonText.startsWith('⚠️ CTVO n°') && responseErrorCode === 3 && responseErrorCode === 100) {
      handleSubmitCTVO(dealDataToSend);
    } else {
      // Flush des corrections du god + rechargement du dossier AVANT d'ouvrir la
      // modale (cf. onBeforeOpen), pour que les champs modifiés dans /review
      // partent bien à MiSIV. Best-effort : on n'empêche pas la soumission si le
      // flush échoue (l'utilisateur peut toujours corriger dans la modale).
      if (onBeforeOpen) {
        try {
          await onBeforeOpen();
        } catch (e) {
          console.warn('SubmitCTVO / onBeforeOpen a échoué (non bloquant) :', e);
        }
      }
      // Purge de l'état du formulaire AVANT de remonter la modale.
      //
      // Les champs de la modale portent leur valeur d'origine en
      // `defaultValue={deal?.X}`. react-hook-form ne lit un `defaultValue`
      // qu'à la PREMIÈRE registration du champ : à la deuxième ouverture, il
      // ressert la valeur mémorisée lors de la première, même si `deal` vient
      // d'être rechargé par `onBeforeOpen` juste au-dessus. D'où le retour de
      // François : on corrige le département de naissance dans /review, on
      // rouvre « Soumettre », et c'est l'ANCIENNE valeur qui s'affiche et qui
      // part à MiSIV.
      //
      // La modale est démontée quand elle est fermée (aucun `keepMounted`), il
      // suffit donc de vider l'état ici : chaque champ se ré-enregistre à
      // l'ouverture suivante et relit son `defaultValue` sur le `deal` frais.
      // On ne recopie PAS les valeurs du deal dans un mapping parallèle : ce
      // mapping devrait redéclarer les 43 champs et divergerait au premier
      // champ ajouté sans qu'aucun test ne le voie.
      // Les champs OBSERVÉS par `watch` au niveau du composant sont réamorcés ici,
      // eux seuls. Ils pilotent l'affichage conditionnel (bloc pro/particulier,
      // co-titulaire) et le `disabled` du département de naissance
      // (`disabled={!isFrance}`). Un `reset()` nu les laisse à `undefined` le
      // temps que les champs se ré-enregistrent : `isFrance` vaut alors false et
      // le département de naissance s'ouvre GRISÉ — constaté en recette sur
      // dev (dossier 11897, 17/09).
      //
      // Les 37 autres champs restent volontairement absents : ils retrouvent
      // leur valeur par le `defaultValue` de leur <Controller>, seule source à
      // maintenir.
      //
      // CoBuyerType n'est injecté QUE s'il existe déjà sur le deal. Un
      // `CoBuyerType: 'person'` forcé (même avec CoBuyer=no) partait dans le
      // body de /api/misiv/save/ctvo et faisait planter le backend sur
      // `.trim()` d'un nom null (prod 2026-09-17, deal 42937 GP-482-JC).
      formMethods.reset({
        SellerType: deal?.SellerType,
        BuyerType: deal?.BuyerType,
        BuyerCountryOfBirth: deal?.BuyerCountryOfBirth ?? 'FRANCE',
        BuyerResidenceType: deal?.BuyerResidenceType ?? 'owner',
        CoBuyer: deal?.CoBuyerType ? 'yes' : 'no',
        ...(deal?.CoBuyerType ? { CoBuyerType: deal.CoBuyerType } : {}),
      });

      validation.validateAndOpen(fetchAndValidateCTVO, () => setIsDialogOpen(true));
    }
    setIsLoading(false);
  };

  const handleManualCTVOSubmit = async () => {
    // Validation selon la méthode choisie
    if (manualCTVOMethod === 'filename') {
      if (!manualReceiptFileName.trim()) {
        openSnackbar('Veuillez entrer un nom de fichier', 'error');
        return;
      }

      // Valider le format du nom de fichier
      if (!manualReceiptFileName.startsWith('CTVO-')) {
        openSnackbar('Le nom de fichier doit commencer par "CTVO-"', 'error');
        return;
      }
    } else if (manualCTVOMethod === 'url') {
      if (!manualReceiptFirebaseUrl.trim()) {
        openSnackbar('Veuillez entrer une URL Firebase', 'error');
        return;
      }

      // Valider que l'URL est bien une URL Firebase
      if (!manualReceiptFirebaseUrl.includes('firebasestorage.googleapis.com')) {
        openSnackbar("L'URL doit être une URL Firebase valide", 'error');
        return;
      }

      // Valider que l'URL contient bien un fichier PDF
      if (!manualReceiptFirebaseUrl.toLowerCase().includes('.pdf')) {
        openSnackbar("L'URL doit pointer vers un fichier PDF", 'error');
        return;
      }
    }

    setIsSubmittingManual(true);

    try {
      const requestData: any = {
        dealId: deal.deal_id || deal.PipedriveDealID,
        excludeFromInvoicing: !shouldInvoiceCTVO,
      };

      if (manualCTVOMethod === 'filename') {
        requestData.receiptFileName = manualReceiptFileName;
      } else {
        requestData.receiptFirebaseUrl = manualReceiptFirebaseUrl;
      }

      const response = await makeAuthenticatedApiCall('post', '/api/cg/ctvo/manual-receipt', requestData);

      if (response.data.success) {
        openSnackbar('Receipt CTVO manuel enregistré avec succès', 'success');
        setIsManualCTVOModalOpen(false);
        setManualReceiptFileName('');
        setManualReceiptFirebaseUrl('');
        setManualCTVOMethod('filename');
        setShouldInvoiceCTVO(true);
        if (manualCTVOMethod === 'filename') {
          setResponseNomFic(manualReceiptFileName);
        }
        setButtonStep(deal.deal_id || deal.PipedriveDealID, 'receipt');
        if (userRole === 'agency') {
          setButtonText(`✅ Télécharger récépissé CTVO `);
        } else {
          setButtonText(`✅ CTVO n°${response.data.data.responseId}`);
        }
        if (onRefresh) {
          onRefresh();
        }
      }
    } catch (error: any) {
      console.error('Erreur lors de la soumission manuelle du CTVO:', error);
      openSnackbar(error.response?.data?.error || 'Erreur lors de la soumission manuelle du CTVO', 'error');
    } finally {
      setIsSubmittingManual(false);
    }
  };

  useEffect(() => {
    if (isDialogOpen) {
      trigger(); // Déclencher la validation de tous les champs
      // Synchroniser l'état local du lieu de naissance avec la valeur du deal
      setBirthPlaceInputValue(deal?.BuyerPlaceOfBirth ?? '');
    }
  }, [isDialogOpen, trigger, deal?.BuyerPlaceOfBirth]);

  return (
    <>
      {buttonSteps[deal.deal_id || deal.PipedriveDealID] === 'waiting-payment' && !isTimeoutError && (
        <Button
          disabled
          sx={{
            fontSize: 14,
            background: '#FF6060',
            color: 'black !important',
            fontWeight: 600,
            width: 234,
            opacity: 1,
          }}
        >
          CTVO : En attente de paiement
        </Button>
      )}
      {buttonSteps[deal.deal_id || deal.PipedriveDealID] === 'waiting-procedures' && !isTimeoutError && (
        <Button
          disabled
          sx={{
            fontSize: 14,
            background: '#FF6060',
            opacity: 0.7,
            color: 'white !important',
            fontWeight: 600,
            width: 260,
          }}
        >
          CTVO : En attente des démarches
        </Button>
      )}
      {buttonSteps[deal.deal_id || deal.PipedriveDealID] === 'initial' && !isTimeoutError && (
        <div style={{ position: 'relative' }}>
          <Tooltip
            title={
              validation.tooltipError ? validation.tooltipError : isNumFormuleExists ? 'Numéro de formule absent' : ''
            }
            placement="top"
          >
            <div
              style={{
                position: 'relative',
                display: 'inline-block',
                width: '100%',
                maxWidth: 160,
                margin: '8px auto 0',
              }}
            >
              <Button
                sx={{
                  fontSize: 14,
                  width: '100%',
                  color: 'white',
                  backgroundColor: validation.tooltipError ? 'red' : '',
                }}
                variant="contained"
                onClick={() => handleButtonClick()}
                disabled={validation.disabledButton || !!validation.tooltipError || isNumFormuleExists}
              >
                {isLoading && <CircularProgress size={24} style={{ color: 'white', marginRight: 1 }} />}
                {validation.disabledButton ? buttonText : '📩 Soumettre le CTVO'}
              </Button>
              {(userRole === 'god' || userRole === 'arbitrator') && (
                <Tooltip title="Enregistrer un CTVO généré en externe" placement="top">
                  <Button
                    sx={{
                      position: 'absolute',
                      top: '-6px',
                      right: '-6px',
                      minWidth: '24px',
                      width: '24px',
                      height: '24px',
                      padding: 0,
                      fontSize: '16px',
                      fontWeight: 'bold',
                      color: 'white',
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: '50%',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                        transform: 'scale(1.1)',
                      },
                      '&:disabled': {
                        backgroundColor: theme.palette.grey[400],
                      },
                      transition: 'all 0.2s ease',
                      zIndex: 1,
                    }}
                    onClick={() => setIsManualCTVOModalOpen(true)}
                    disabled={validation.disabledButton || !!validation.tooltipError || isNumFormuleExists}
                  >
                    +
                  </Button>
                </Tooltip>
              )}
            </div>
          </Tooltip>
          {validation.tooltipError && (
            <Typography
              color="error"
              sx={{
                fontSize: 12,
                marginTop: 1,
                textAlign: 'center',
              }}
            >
              {validation.tooltipError}
            </Typography>
          )}
        </div>
      )}

      {buttonSteps[deal.deal_id || deal.PipedriveDealID] === 'save' && !isTimeoutError && (
        <Tooltip
          title={
            isLoading
              ? "Demande sauvegardée, maintenant en cours d'envoi"
              : "La demande n'a pas abouti. Cliquez pour réessayer."
          }
          placement="top"
        >
          <div>
            <Button
              sx={{
                fontSize: 14,
                margin: '8px auto 0',
                width: 180,
                backgroundColor: 'orange !important',
              }}
              variant="contained"
              onClick={responseErrorCode !== 3 && responseErrorCode !== 100 ? handleButtonClick : handleSubmitCTVO}
              fullWidth
            >
              {isLoading && <CircularProgress size={12} style={{ color: 'white' }} />}
              {buttonText}
            </Button>
            {errorText && (
              <Typography
                variant="body2"
                sx={{ fontSize: '10px', lineHeight: '1.25', marginTop: 1, fontWeight: 'bold', color: 'red !important' }}
              >
                {errorText}
              </Typography>
            )}
          </div>
        </Tooltip>
      )}

      {(buttonSteps[deal.deal_id || deal.PipedriveDealID] === 'receipt' ||
        buttonSteps[deal.deal_id || deal.PipedriveDealID] === 'receipt-only') &&
        !isTimeoutError && (
          <>
            <Button
              sx={{
                fontSize: 14,
                margin: '8px auto 0',
                maxWidth: 160,
                backgroundColor: 'green',
                color: 'white',
              }}
              variant="contained"
              onClick={() => handleGetReceipt(responseNomFic)}
              fullWidth
            >
              {isLoading && <CircularProgress size={12} style={{ color: 'white' }} />}
              {buttonText}
            </Button>
            {hasFirebaseUrlOnly && userRole !== 'agency' && (
              <Tooltip title="Il y a eu un soucis au moment où la démarche a été envoyée à Misiv qui fait que nous n'avons pas le nom du fichier, mais nous avons quand même pu sauvegarder le récépissé sur Firebase">
                <ErrorOutlineIcon
                  sx={{
                    color: theme.palette.error.main,
                    fontSize: '16px',
                    ml: 0.5,
                    opacity: 0.8,
                    '&:hover': {
                      opacity: 1,
                    },
                  }}
                />
              </Tooltip>
            )}
          </>
        )}
      {isTimeoutError && responseId !== undefined && (
        <Tooltip title="Timeout Ministère : Ré-essayez dans quelques instants" placement="top">
          <div>
            <Button
              sx={{
                fontSize: 14,
                margin: '8px auto 0',
                width: 180,
                backgroundColor: 'red !important',
              }}
              variant="contained"
              onClick={() => handleResendToMinistry(responseId)}
              fullWidth
            >
              {isLoading && <CircularProgress size={12} style={{ color: 'white' }} />}
              {buttonText}
            </Button>
            {errorText && (
              <Typography
                variant="body2"
                sx={{ fontSize: '10px', lineHeight: '1.25', marginTop: 0, fontWeight: 'bold', color: 'red !important' }}
              >
                {errorText}
              </Typography>
            )}
          </div>
        </Tooltip>
      )}
      {/*errorText && (
          <Typography
            variant="body2"
            sx={{ fontSize: '10px', lineHeight: '1.25', marginTop: 1, fontWeight: 'bold', color: 'red' }}
          >
            {errorText}
          </Typography>
        )*/}

      <Dialog open={isDialogOpen} onClose={handleDialogClose} fullWidth maxWidth="lg">
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(handleSave)}>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={12} sx={{ textAlign: 'center' }}>
                  <Controller
                    name="purchaseDateTime"
                    control={control}
                    defaultValue={purchaseDateTime} // Ensure a default value is set
                    render={({ field }) => (
                      <TextField
                        {...field} // Bind react-hook-form's field to the TextField
                        autoFocus
                        margin="dense"
                        id="purchaseDateTime"
                        label="Date et heure de la vente"
                        type="datetime-local"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                  <Typography variant="body2" textAlign="center">
                    Veuillez vérifier les informations suivantes :
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Controller
                    name="RegPlate"
                    control={control}
                    defaultValue={deal?.RegPlate}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="inputElement"
                        fullWidth
                        margin="dense"
                        id="RegPlate"
                        label="Plaque d'immatriculation"
                        type="text"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Controller
                    name="VIN"
                    control={control}
                    defaultValue={deal?.VIN}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="inputElement"
                        fullWidth
                        margin="dense"
                        id="VIN"
                        label="VIN"
                        type="text"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Controller
                    name="NumFormule"
                    control={control}
                    defaultValue={deal?.NumFormule}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="inputElement"
                        fullWidth
                        margin="dense"
                        id="NumFormule"
                        label="Numéro de Formule"
                        type="text"
                      />
                    )}
                  />
                </Grid>
                {deal?.SellerType && (
                  <>
                    <Grid container sx={{ marginTop: 2 }} alignItems="center" item xs={12} md={12}>
                      <Grid item>
                        <Typography variant="body1" component="div" sx={{ marginRight: 2 }}>
                          Vendeur
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Controller
                          name="SellerType"
                          control={control}
                          defaultValue={deal?.SellerType}
                          render={({ field }) => (
                            <RadioGroup {...field} row>
                              <FormControlLabel
                                className="inputElement"
                                value="person"
                                control={<Radio />}
                                label="Particulier"
                              />
                              <FormControlLabel
                                className="inputElement"
                                value="pro"
                                control={<Radio />}
                                label="Professionnel"
                              />
                            </RadioGroup>
                          )}
                        />
                      </Grid>
                    </Grid>
                    {SellerType === 'person' && (
                      <>
                        <Grid item xs={12} md={2}>
                          <Controller
                            name="SellerGender"
                            control={control}
                            defaultValue={genderBlockDefault(deal?.SellerType, deal?.SellerGender)}
                            rules={{}}
                            render={({ field }) => (
                              <Select
                                error={!!errors.gender}
                                sx={{ minHeight: 'inherit', height: '56px', marginTop: '8px' }}
                                {...field}
                                fullWidth
                                className="inputElement"
                                label="Sexe"
                              >
                                <MenuItem value="male">Masculin</MenuItem>
                                <MenuItem value="female">Féminin</MenuItem>
                              </Select>
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Controller
                            name="SellerLastName"
                            rules={validationRules.firstName}
                            control={control}
                            defaultValue={personBlockDefault(deal?.SellerType, deal?.SellerLastName)}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                className="inputElement"
                                margin="dense"
                                id="SellerLastName"
                                error={!!errors.firstName}
                                label="Nom"
                                type="text"
                                fullWidth
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <Controller
                            name="SellerFirstName"
                            rules={validationRules.firstName}
                            control={control}
                            defaultValue={personBlockDefault(deal?.SellerType, deal?.SellerFirstName)}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                className="inputElement"
                                margin="dense"
                                id="SellerFirstName"
                                error={!!errors.firstName}
                                label="Prénom"
                                type="text"
                                fullWidth
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Controller
                            name="SellerUsualName"
                            rules={{}}
                            control={control}
                            defaultValue={personBlockDefault(deal?.SellerType, deal?.SellerUsualName)}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                className="inputElement"
                                margin="dense"
                                id="SellerUsualName"
                                error={!!errors.firstName}
                                label="Nom d'usage"
                                type="text"
                                fullWidth
                              />
                            )}
                          />
                        </Grid>
                      </>
                    )}
                    {SellerType === 'pro' && (
                      <>
                        <Grid item xs={12} md={5}>
                          <Controller
                            name="SellerSIRET"
                            control={control}
                            rules={
                              // Valeur COCHÉE, pas celle de la base : l'exploitant bascule le
                              // radio sans que `deal` change, et la règle `required` ne suivait
                              // pas. Le bouton restait actif et l'erreur ne tombait qu'au retour
                              // du serveur (retour Luc 2026-09-15, dossier pro → pro).
                              SellerType === 'pro'
                                ? {
                                    required: 'Ce champ est requis',
                                    minLength: {
                                      value: 14,
                                      message: 'Le SIRET doit contenir exactement 14 caractères',
                                    },
                                    maxLength: {
                                      value: 14,
                                      message: 'Le SIRET doit contenir exactement 14 caractères',
                                    },
                                  }
                                : {
                                    minLength: {
                                      value: 14,
                                      message: 'Le SIRET doit contenir exactement 14 caractères',
                                    },
                                    maxLength: {
                                      value: 14,
                                      message: 'Le SIRET doit contenir exactement 14 caractères',
                                    },
                                  }
                            }
                            defaultValue={companyBlockDefault(deal?.SellerType, deal?.SellerSIRET)}
                            render={({ field }) => (
                              <TextField
                                className="inputElement"
                                error={!!errors.SellerSIRET}
                                helperText={errors.SellerSIRET ? (errors.SellerSIRET.message as string) : ''}
                                {...field}
                                margin="dense"
                                id="SellerSIRET"
                                label="SIRET"
                                type="text"
                                fullWidth
                                inputProps={{ maxLength: 14 }} // Limite le nombre de caractères que l'utilisateur peut taper
                                onChange={(e) => field.onChange(removeSpaces(e.target.value))} // Enlève les espaces
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} md={5}>
                          <Controller
                            name="SellerCompanyName"
                            rules={SellerType === 'pro' ? { required: 'Ce champ est requis' } : {}}
                            control={control}
                            defaultValue={companyBlockDefault(deal?.SellerType, deal?.SellerCompanyName)}
                            render={({ field }) => (
                              <TextField
                                className="inputElement"
                                {...field}
                                margin="dense"
                                error={!!errors.SellerCompanyName}
                                id="SellerCompanyName"
                                label="Nom de l'entreprise"
                                type="text"
                                fullWidth
                              />
                            )}
                          />
                        </Grid>
                      </>
                    )}
                  </>
                )}
                <Grid container sx={{ marginTop: 2 }} alignItems="center" item xs={12} md={12}>
                  <Grid item>
                    <Typography variant="body1" component="div" sx={{ marginRight: 2 }}>
                      Acheteur
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Controller
                      name="BuyerType"
                      control={control}
                      defaultValue={deal?.BuyerType}
                      render={({ field }) => (
                        <RadioGroup {...field} row>
                          <FormControlLabel
                            className="inputElement"
                            value="person"
                            control={<Radio />}
                            label="Particulier"
                          />
                          <FormControlLabel
                            className="inputElement"
                            value="pro"
                            control={<Radio />}
                            label="Professionnel"
                          />
                        </RadioGroup>
                      )}
                    />
                  </Grid>
                </Grid>
                {BuyerType === 'pro' && (
                  <>
                    <Grid item xs={12} md={5}>
                      <Controller
                        name="BuyerSIRET"
                        control={control}
                        rules={
                          // Idem vendeur : on suit le radio, pas la base.
                          BuyerType === 'pro'
                            ? {
                                required: 'Ce champ est requis',
                                minLength: {
                                  value: 14,
                                  message: 'Le SIRET doit contenir exactement 14 caractères',
                                },
                                maxLength: {
                                  value: 14,
                                  message: 'Le SIRET doit contenir exactement 14 caractères',
                                },
                              }
                            : {
                                minLength: {
                                  value: 14,
                                  message: 'Le SIRET doit contenir exactement 14 caractères',
                                },
                                maxLength: {
                                  value: 14,
                                  message: 'Le SIRET doit contenir exactement 14 caractères',
                                },
                              }
                        }
                        defaultValue={companyBlockDefault(deal?.BuyerType, deal?.BuyerSIRET)}
                        render={({ field }) => (
                          <TextField
                            className="inputElement"
                            error={!!errors.BuyerSIRET}
                            helperText={errors.BuyerSIRET ? (errors.BuyerSIRET.message as string) : ''}
                            {...field}
                            margin="dense"
                            id="BuyerSIRET"
                            label="SIRET"
                            type="text"
                            fullWidth
                            inputProps={{ maxLength: 14 }} // Limite le nombre de caractères que l'utilisateur peut taper
                            onChange={(e) => field.onChange(removeSpaces(e.target.value))} // Enlève les espaces
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <Controller
                        name="BuyerCompanyName"
                        rules={BuyerType === 'pro' ? { required: 'Ce champ est requis' } : {}}
                        control={control}
                        defaultValue={companyBlockDefault(deal?.BuyerType, deal?.BuyerCompanyName)}
                        render={({ field }) => (
                          <TextField
                            className="inputElement"
                            {...field}
                            margin="dense"
                            error={!!errors.BuyerCompanyName}
                            id="BuyerCompanyName"
                            label="Nom de l'entreprise"
                            type="text"
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}
                {BuyerType === 'person' && (
                  <>
                    <Grid item xs={12} md={4}>
                      <Controller
                        name="BuyerLastName"
                        rules={validationRules.lastName}
                        control={control}
                        defaultValue={personBlockDefault(deal?.BuyerType, deal?.BuyerLastName)}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            id="BuyerLastName"
                            error={!!errors.lastName}
                            className="inputElement"
                            margin="dense"
                            label="Nom"
                            type="text"
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="BuyerFirstName"
                        rules={validationRules.firstName}
                        control={control}
                        defaultValue={personBlockDefault(deal?.BuyerType, deal?.BuyerFirstName)}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            className="inputElement"
                            margin="dense"
                            id="BuyerFirstName"
                            error={!!errors.firstName}
                            label="Prénom"
                            type="text"
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="BuyerUsualName"
                        rules={{}}
                        control={control}
                        defaultValue={personBlockDefault(deal?.BuyerType, deal?.BuyerUsualName)}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            className="inputElement"
                            margin="dense"
                            id="BuyerUsualName"
                            error={!!errors.firstName}
                            label="Nom d'usage"
                            type="text"
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12} md={2}></Grid>
                <Grid item xs={12} md={2}>
                  <Controller
                    name="BuyerAddressNumber"
                    control={control}
                    rules={validationRules.addressNumber}
                    defaultValue={deal?.BuyerAddressNumber}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="inputElement"
                        error={!!errors.addressNumber}
                        margin="dense"
                        id="BuyerAddressNumber"
                        label="Numéro d'adresse"
                        type="text"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Controller
                    name="BuyerAddressExtension"
                    control={control}
                    defaultValue={deal?.BuyerAddressExtension ?? ''}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="inputElement"
                        margin="dense"
                        id="BuyerAddressExtension"
                        label="Extension d'adresse"
                        type="text"
                        fullWidth
                        inputProps={{ maxLength: 10 }}
                        placeholder="Ex: BIS"
                        InputLabelProps={{ shrink: true }}
                        onInput={(e) => {
                          const input = e.target as HTMLInputElement;
                          input.value = input.value.replace(/\d/g, '');
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Controller
                    name="BuyerAddressType"
                    control={control}
                    // Normalisation pour tolérer les CG historiques stockées
                    // avec un libellé long ("BOULEVARD") au lieu du code court
                    // attendu par le Select ("blvd"). Cf. utils/normalizeTypeVoie.ts.
                    defaultValue={normalizeTypeVoie(deal?.BuyerAddressType) || (deal?.BuyerAddressType ?? '')}
                    rules={validationRules.addressType}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl fullWidth margin="dense" error={!!error}>
                        <InputLabel className="inputElement" id={`${field.name}-label`}>
                          Type d'adresse
                        </InputLabel>
                        <Select
                          className="inputElement"
                          {...field}
                          sx={{ minHeight: 'inherit', height: '56px', marginTop: '0px' }}
                          labelId={`${field.name}-label`}
                          id={field.name}
                          label="Type d'adresse"
                          value={field.value ? field.value.toLowerCase() : ''}
                        >
                          <MenuItem value="rue">Rue</MenuItem>
                          <MenuItem value="all">Allée</MenuItem>
                          <MenuItem value="avn">Avenue</MenuItem>
                          <MenuItem value="blvd">Boulevard</MenuItem>
                          <MenuItem value="chau">Chaussée</MenuItem>
                          <MenuItem value="che">Chemin</MenuItem>
                          <MenuItem value="cour">Cour</MenuItem>
                          <MenuItem value="fbg">Faubourg</MenuItem>
                          <MenuItem value="fort">Fort</MenuItem>
                          <MenuItem value="imp">Impasse</MenuItem>
                          <MenuItem value="ldit">Lieu-Dit</MenuItem>
                          <MenuItem value="parc">Parc</MenuItem>
                          <MenuItem value="pass">Passage</MenuItem>
                          <MenuItem value="plc">Place</MenuItem>
                          <MenuItem value="quai">Quai</MenuItem>
                          <MenuItem value="rte">Route</MenuItem>
                          <MenuItem value="sent">Sentier</MenuItem>
                          <MenuItem value="squa">Square</MenuItem>
                          <MenuItem value="sans">Inconnu</MenuItem>
                        </Select>
                        {error && <FormHelperText>{error.message}</FormHelperText>}
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="BuyerAddressName"
                    control={control}
                    rules={validationRules.addressName}
                    defaultValue={deal?.BuyerAddressName}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        error={!!errors.addressName}
                        className="inputElement"
                        margin="dense"
                        id="BuyerAddressName"
                        label="Nom de la voie"
                        type="text"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="BuyerAddressPlace"
                    control={control}
                    rules={{}}
                    defaultValue={deal?.BuyerAddressPlace ?? ''}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        error={!!error}
                        helperText={error ? error.message : ''}
                        className="inputElement"
                        margin="dense"
                        id="BuyerAddressPlace"
                        label="Lieu-dit"
                        type="text"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="BuyerAddressStairs"
                    control={control}
                    rules={{}}
                    defaultValue={deal?.BuyerAddressStairs ?? ''}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        error={!!error}
                        helperText={error ? error.message : ''}
                        className="inputElement"
                        margin="dense"
                        id="BuyerAddressStairs"
                        label="Étage/Escalier/Appartement"
                        type="text"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="BuyerAddressComplement"
                    control={control}
                    rules={{}}
                    defaultValue={deal?.BuyerAddressComplement ?? ''}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        error={!!error}
                        helperText={error ? error.message : ''}
                        className="inputElement"
                        margin="dense"
                        id="BuyerAddressComplement"
                        label="Complément d'adresse"
                        type="text"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="BuyerAddressPostalBox"
                    control={control}
                    rules={{}}
                    defaultValue={deal?.BuyerAddressPostalBox ?? ''}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        error={!!error}
                        helperText={error ? error.message : ''}
                        className="inputElement"
                        margin="dense"
                        id="BuyerAddressPostalBox"
                        label="Boîte postale"
                        type="text"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Controller
                    name="BuyerCity"
                    control={control}
                    rules={validationRules.city}
                    defaultValue={deal?.BuyerCity}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        error={!!errors.city}
                        className="inputElement"
                        margin="dense"
                        id="BuyerCity"
                        label="Ville"
                        type="text"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Controller
                    name="BuyerPostalCode"
                    control={control}
                    rules={validationRules.postalCode}
                    defaultValue={deal?.BuyerPostalCode}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        error={!!errors.postalCode}
                        className="inputElement"
                        margin="dense"
                        id="BuyerPostalCode"
                        label="Code postal"
                        type="text"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="BuyerCountry"
                    control={control}
                    rules={validationRules.country}
                    defaultValue={deal?.BuyerCountry}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="inputElement"
                        margin="dense"
                        id="BuyerCountry"
                        error={!!errors.country}
                        label="Pays"
                        type="text"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3} marginTop="8px">
                  <Controller
                    name="BuyerPhone"
                    control={control}
                    // Format international +33 systématique (retour François) : le
                    // widget attend "33XXXXXXXXX" (indicatif sans + ni 0) pour
                    // afficher « +33 6 … » ; la valeur brute "06…" cassait l'affichage.
                    defaultValue={toFrPhoneInputValue(deal?.BuyerPhone)}
                    rules={{
                      pattern: {
                        value: /^[0-9]+$/,
                        message: 'Numéro de téléphone invalide',
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <PhoneInput
                        {...field}
                        country={'fr'}
                        onlyCountries={['fr']}
                        placeholder="Téléphone portable"
                        inputProps={{
                          name: 'phone',
                          autoFocus: true,
                        }}
                        specialLabel=""
                        containerStyle={{ width: '100%' }}
                        inputStyle={{
                          width: '100%',
                          fontFamily: 'Urbanist',
                          fontWeight: 500,
                          color: 'black',
                          height: '56px',
                          borderColor: error ? 'red' : '',
                        }}
                        buttonStyle={{ height: '56px' }}
                      />
                    )}
                  />
                </Grid>
                {BuyerType === 'person' && (
                  <>
                    <Grid item xs={12} md={2}>
                      <Controller
                        name="BuyerGender"
                        control={control}
                        defaultValue={genderBlockDefault(deal?.BuyerType, deal?.BuyerGender)}
                        rules={BuyerType === 'person' ? { required: 'Ce champ est requis' } : {}}
                        render={({ field }) => (
                          <Select
                            error={!!errors.gender}
                            {...field}
                            margin="dense"
                            sx={{ minHeight: 'inherit', height: '56px', marginTop: '8px' }}
                            fullWidth
                            className="inputElement"
                            label="Sexe"
                          >
                            <MenuItem value="male">Masculin</MenuItem>
                            <MenuItem value="female">Féminin</MenuItem>
                          </Select>
                        )}
                      />
                    </Grid>
                    {/* Type d'hébergement */}
                    <Grid container alignItems="center" item xs={12} md={12}>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" component="div" sx={{ marginRight: 2 }}>
                          Type d'hébergement :
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={9}>
                        <Controller
                          name="BuyerResidenceType"
                          control={control}
                          defaultValue={deal?.BuyerResidenceType ?? 'owner'}
                          render={({ field }) => (
                            <RadioGroup {...field} row>
                              <FormControlLabel
                                className="inputElement"
                                value="owner"
                                control={<Radio />}
                                label="Titulaire"
                              />
                              <FormControlLabel
                                className="inputElement"
                                value="guest"
                                control={<Radio />}
                                label="Hébergé"
                              />
                            </RadioGroup>
                          )}
                        />
                      </Grid>
                    </Grid>
                    {/* Date de naissance */}
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="BuyerDateOfBirth"
                        control={control}
                        rules={BuyerType === 'person' ? { required: 'Ce champ est requis' } : {}}
                        defaultValue={deal?.BuyerDateOfBirth && deal?.BuyerDateOfBirth.substring(0, 10)}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            id="BuyerDateOfBirth"
                            className="inputElement"
                            error={!!errors.BuyerDateOfBirth}
                            helperText={errors.BuyerDateOfBirth ? (errors.BuyerDateOfBirth.message as string) : ''}
                            label="Date de naissance"
                            type="date"
                            fullWidth
                            margin="dense"
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                              min: new Date(new Date().getFullYear() - 120, 0, 1).toISOString().slice(0, 10),
                              max: new Date(new Date().getFullYear() - 17, 11, 31).toISOString().slice(0, 10),
                            }}
                          />
                        )}
                      />
                    </Grid>
                    {/* Lieu de naissance avec autocomplete */}
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="BuyerPlaceOfBirth"
                        rules={BuyerType === 'person' ? { required: 'Ce champ est requis' } : {}}
                        control={control}
                        defaultValue={deal?.BuyerPlaceOfBirth ?? ''}
                        render={({ field }) => (
                          <Autocomplete
                            disablePortal
                            freeSolo
                            className="inputElement"
                            id="BuyerPlaceOfBirth"
                            options={birthDepartmentOptions}
                            getOptionLabel={(option: any) =>
                              typeof option === 'string' ? option : `${option.label} (${option.value})`
                            }
                            isOptionEqualToValue={(option, value) =>
                              typeof value === 'string' ? option.label === value : option.label === value.label
                            }
                            inputValue={birthPlaceInputValue}
                            onInputChange={(_event, newInputValue, reason) => {
                              setBirthPlaceInputValue(newInputValue);
                              // Mettre à jour le champ react-hook-form en temps réel
                              field.onChange(newInputValue);
                              // Lancer la recherche API si assez de caractères
                              if (reason !== 'reset') {
                                handleBirthCityChange(_event, newInputValue);
                              }
                              // Si on efface complètement, réinitialiser le département
                              if (!newInputValue) {
                                setValue('BuyerDepartmentOfBirth', '', {
                                  shouldValidate: true,
                                  shouldDirty: true,
                                });
                              }
                            }}
                            onChange={(_event, value) => {
                              if (typeof value === 'string') {
                                field.onChange(value);
                                setBirthPlaceInputValue(value);
                              } else if (value) {
                                field.onChange(value.label);
                                setBirthPlaceInputValue(value.label);
                                const department = value.value.slice(0, 2);
                                setValue('BuyerDepartmentOfBirth', department, {
                                  shouldValidate: true,
                                  shouldDirty: true,
                                });
                              } else {
                                field.onChange('');
                                setBirthPlaceInputValue('');
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                sx={{
                                  minHeight: 'inherit',
                                  height: '56px',
                                  '& .MuiOutlinedInput-root': { height: '56px' },
                                }}
                                error={!!errors.BuyerPlaceOfBirth}
                                helperText={
                                  errors.BuyerPlaceOfBirth ? (errors.BuyerPlaceOfBirth.message as string) : ''
                                }
                                margin="dense"
                                label="Lieu de naissance"
                                fullWidth
                              />
                            )}
                          />
                        )}
                      />
                    </Grid>
                    {/* Numéro de département de naissance */}
                    <Grid item xs={12} md={2}>
                      <Controller
                        name="BuyerDepartmentOfBirth"
                        rules={
                          BuyerType === 'person'
                            ? {
                                validate: {
                                  required: (value) =>
                                    isFrance
                                      ? !value || value.trim() === ''
                                        ? 'Ce champ est requis pour la France'
                                        : true
                                      : true,
                                  departmentFormat: (value) => {
                                    if (!isFrance || !value || !value.trim()) return true;
                                    const deptRegex = /^(?:[0-9]{2,3}|2[AB])$/;
                                    return deptRegex.test(value.trim()) || 'Format invalide (ex: 01, 77, 971, 2A, 2B)';
                                  },
                                },
                              }
                            : {}
                        }
                        control={control}
                        defaultValue={deal?.BuyerDepartmentOfBirth ?? ''}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            onChange={(e) => {
                              const value = e.target.value.toUpperCase();
                              const filteredValue = value.replace(/[^0-9AB]/g, '').slice(0, 3);
                              field.onChange(filteredValue);
                            }}
                            className="inputElement"
                            error={!!error}
                            helperText={error ? error.message : ''}
                            margin="dense"
                            id="BuyerDepartmentOfBirth"
                            label="N° département naissance"
                            type="text"
                            fullWidth
                            inputProps={{ maxLength: 3 }}
                            disabled={!isFrance}
                            InputLabelProps={{
                              style: {
                                fontWeight: 'bold',
                                fontSize: '0.9rem',
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                    {/* Pays de naissance */}
                    <Grid item xs={12} md={4}>
                      <Controller
                        name="BuyerCountryOfBirth"
                        rules={BuyerType === 'person' ? { required: 'Ce champ est requis' } : {}}
                        control={control}
                        defaultValue={deal?.BuyerCountryOfBirth ?? 'FRANCE'}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            className="inputElement"
                            error={!!errors.BuyerCountryOfBirth}
                            helperText={
                              errors.BuyerCountryOfBirth ? (errors.BuyerCountryOfBirth.message as string) : ''
                            }
                            margin="dense"
                            id="BuyerCountryOfBirth"
                            label="Pays de naissance"
                            type="text"
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}
                <Grid container sx={{ marginTop: 2 }} alignItems="center" item xs={12} md={12}>
                  <Grid item>
                    <Typography variant="body1" component="div" sx={{ marginRight: 2 }}>
                      Il y a un co-titulaire :
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Controller
                      name="CoBuyer"
                      control={control}
                      defaultValue={deal?.CoBuyerType ? 'yes' : 'no'}
                      render={({ field }) => (
                        <RadioGroup {...field} row>
                          <FormControlLabel className="inputElement" value="no" control={<Radio />} label="Non" />
                          <FormControlLabel className="inputElement" value="yes" control={<Radio />} label="Oui" />
                        </RadioGroup>
                      )}
                    />
                  </Grid>
                </Grid>
                {CoBuyer === 'yes' && (
                  <>
                    <Grid container sx={{ marginTop: 2 }} alignItems="center" item xs={12} md={12}>
                      <Grid item>
                        <Typography variant="h6" sx={{ marginRight: 2 }}>
                          Le co-titulaire est un :
                        </Typography>
                      </Grid>

                      <Grid item xs={6} sm={4}>
                        <Controller
                          name="CoBuyerType"
                          control={control}
                          defaultValue="person"
                          render={({ field }) => (
                            <RadioGroup {...field} row>
                              <FormControlLabel
                                className="inputElement"
                                value="person"
                                control={<Radio />}
                                label="Particulier"
                              />
                              <FormControlLabel
                                className="inputElement"
                                value="pro"
                                control={<Radio />}
                                label="Professionnel"
                              />
                            </RadioGroup>
                          )}
                        />
                      </Grid>
                    </Grid>
                    {CoBuyerType === 'person' && (
                      <>
                        <Grid item xs={12} md={3}>
                          <Controller
                            name="CoBuyerGender"
                            control={control}
                            defaultValue={genderBlockDefault(deal?.CoBuyerType, deal?.CoBuyerGender)}
                            rules={{}}
                            render={({ field }) => (
                              <Select
                                error={!!errors.gender}
                                sx={{ minHeight: 'inherit', height: '56px', marginTop: '8px' }}
                                {...field}
                                margin="dense"
                                fullWidth
                                className="inputElement"
                                label="Sexe"
                              >
                                <MenuItem value="male">Masculin</MenuItem>
                                <MenuItem value="female">Féminin</MenuItem>
                              </Select>
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Controller
                            name="CoBuyerLastName"
                            rules={validationRules.firstName}
                            control={control}
                            defaultValue={personBlockDefault(deal?.CoBuyerType, deal?.CoBuyerLastName)}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                className="inputElement"
                                margin="dense"
                                id="CoBuyerLastName"
                                error={!!errors.firstName}
                                label="Nom"
                                type="text"
                                fullWidth
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Controller
                            name="CoBuyerFirstName"
                            rules={validationRules.firstName}
                            control={control}
                            defaultValue={personBlockDefault(deal?.CoBuyerType, deal?.CoBuyerFirstName)}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                className="inputElement"
                                margin="dense"
                                id="CoBuyerFirstName"
                                error={!!errors.firstName}
                                label="Prénom"
                                type="text"
                                fullWidth
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Controller
                            name="CoBuyerUsualName"
                            rules={{}}
                            control={control}
                            defaultValue={personBlockDefault(deal?.CoBuyerType, deal?.CoBuyerUsualName)}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                className="inputElement"
                                margin="dense"
                                id="CoBuyerUsualName"
                                error={!!errors.firstName}
                                label="Nom d'usage"
                                type="text"
                                fullWidth
                              />
                            )}
                          />
                        </Grid>
                      </>
                    )}
                    {CoBuyerType === 'pro' && (
                      <>
                        <Grid item xs={12} md={6}>
                          <Controller
                            name="CoBuyerSIRET"
                            control={control}
                            rules={
                              deal?.CoBuyerType === 'pro'
                                ? {
                                    required: 'Ce champ est requis',
                                    minLength: {
                                      value: 14,
                                      message: 'Le SIRET doit contenir exactement 14 caractères',
                                    },
                                    maxLength: {
                                      value: 14,
                                      message: 'Le SIRET doit contenir exactement 14 caractères',
                                    },
                                  }
                                : {
                                    minLength: {
                                      value: 14,
                                      message: 'Le SIRET doit contenir exactement 14 caractères',
                                    },
                                    maxLength: {
                                      value: 14,
                                      message: 'Le SIRET doit contenir exactement 14 caractères',
                                    },
                                  }
                            }
                            defaultValue={companyBlockDefault(deal?.CoBuyerType, deal?.CoBuyerSIRET)}
                            render={({ field }) => (
                              <TextField
                                className="inputElement"
                                error={!!errors.CoBuyerSIRET}
                                {...field}
                                margin="dense"
                                id="CoBuyerSIRET"
                                label="SIRET"
                                type="text"
                                fullWidth
                                inputProps={{ maxLength: 14 }} // Limite le nombre de caractères que l'utilisateur peut taper
                                onChange={(e) => field.onChange(removeSpaces(e.target.value))} // Enlève les espaces
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Controller
                            name="CoBuyerCompanyName"
                            rules={deal?.CoBuyerType === 'pro' ? { required: 'Ce champ est requis' } : {}}
                            control={control}
                            defaultValue={companyBlockDefault(deal?.CoBuyerType, deal?.CoBuyerCompanyName)}
                            render={({ field }) => (
                              <TextField
                                className="inputElement"
                                {...field}
                                margin="dense"
                                error={!!errors.CoBuyerCompanyName}
                                id="CoBuyerCompanyName"
                                label="Nom de l'entreprise"
                                type="text"
                                fullWidth
                              />
                            )}
                          />
                        </Grid>
                      </>
                    )}
                  </>
                )}
                <Grid item xs={12} md={12}>
                  <Typography variant="body2">
                    {BuyerType === 'pro'
                      ? 'KBIS'
                      : "Justificatif de domicile, Passeport ou Carte d'identité (Recto-Verso)"}
                  </Typography>
                  <Button
                    onClick={handleDownloadPhotos}
                    style={{
                      position: 'relative',
                      float: 'right',
                      right: '0px',
                      top: '0px',
                      backgroundColor: theme.palette.saffron.main,
                      zIndex: 1000,
                      cursor: 'pointer',
                    }}
                  >
                    {downloading ? (
                      <CircularProgress size={24} style={{ color: 'white', marginRight: 1 }} />
                    ) : (
                      <SaveIcon color="action" />
                    )}
                  </Button>
                  <CfnPiecesWarning />
                  <FirebaseUploadComponent
                    onBeforeDelete={handleBeforeDelete}
                    onDeleteComplete={handleDeleteComplete}
                    onUploadComplete={handleUploadComplete}
                    uploadedUrls={uploadedUrls}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button className="secondaryDealActionButton" onClick={handleDialogClose}>
                Annuler
              </Button>
              <Button
                className="dealActionButton"
                variant="contained"
                onClick={handleSaveData}
                disabled={isSavingData}
                sx={{
                  border: '2px solid #FF6060',
                  '&:hover': {
                    border: '2px solid #FF6060',
                  },
                }}
              >
                {isSavingData && <CircularProgress size={24} style={{ color: 'white', marginRight: 8 }} />}
                Enregistrer
              </Button>
              <Button
                className="dealActionButton"
                variant="contained"
                onClick={handleConfirmClick}
                disabled={!isValid || !purchaseDateTime || isLoading}
              >
                {isLoading && <CircularProgress size={24} style={{ color: 'white', marginRight: 1 }} />}Soumettre
              </Button>
            </DialogActions>
          </form>
        </FormProvider>
      </Dialog>
      {/* Modale de confirmation */}
      <Dialog open={isConfirmModalOpen} onClose={() => setIsConfirmModalOpen(false)}>
        <DialogContent>
          <Typography>Êtes-vous sûr que toutes les informations sont correctes ?</Typography>
        </DialogContent>
        <DialogActions>
          <Button className="secondaryDealActionButton" onClick={() => setIsConfirmModalOpen(false)}>
            Annuler
          </Button>
          <Button className="dealActionButton" variant="contained" onClick={handleFinalConfirm}>
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modale de soumission manuelle du CTVO */}
      <Dialog
        open={isManualCTVOModalOpen}
        onClose={() => !isSubmittingManual && setIsManualCTVOModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Enregistrer un CTVO généré en externe
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Choisissez la méthode selon les informations dont vous disposez
          </Typography>

          {/* Choix facturation */}
          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
              Ce CTVO doit-il être intégré dans une facture ?
            </Typography>
            <RadioGroup
              value={shouldInvoiceCTVO ? 'yes' : 'no'}
              onChange={(e) => setShouldInvoiceCTVO(e.target.value === 'yes')}
            >
              <FormControlLabel
                className="inputElement"
                value="yes"
                control={<Radio />}
                label="Oui, intégrer dans la facturation"
                disabled={isSubmittingManual}
              />
              <FormControlLabel
                className="inputElement"
                value="no"
                control={<Radio />}
                label="Non, ne pas facturer (déjà facturé manuellement)"
                disabled={isSubmittingManual}
              />
            </RadioGroup>
          </FormControl>

          {/* Sélection de la méthode */}
          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
              Comment soumettre le CTVO ?
            </Typography>
            <RadioGroup
              row
              value={manualCTVOMethod}
              onChange={(e) => setManualCTVOMethod(e.target.value as 'filename' | 'url')}
            >
              <FormControlLabel
                value="filename"
                control={<Radio />}
                label="Nom du fichier"
                disabled={isSubmittingManual}
              />
              <FormControlLabel value="url" control={<Radio />} label="URL Firebase" disabled={isSubmittingManual} />
            </RadioGroup>
          </FormControl>

          {/* Champ pour le nom de fichier */}
          {manualCTVOMethod === 'filename' && (
            <Box>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontSize: '0.875rem' }}>
                Entrez le nom du fichier PDF du CTVO généré en externe. Le système téléchargera le PDF depuis le
                ministère et l'uploadera sur Firebase.
              </Typography>
              <TextField
                autoFocus
                fullWidth
                className="inputElement"
                label="Nom du fichier CTVO"
                value={manualReceiptFileName}
                onChange={(e) => setManualReceiptFileName(e.target.value)}
                placeholder="Ex: CTVO-AE_DZ-669-HY_2025-11-28-171438.pdf"
                disabled={isSubmittingManual}
                helperText="Le nom doit commencer par 'CTVO-'"
                error={manualReceiptFileName.length > 0 && !manualReceiptFileName.startsWith('CTVO-')}
                margin="dense"
              />
            </Box>
          )}

          {/* Champ pour l'URL Firebase */}
          {manualCTVOMethod === 'url' && (
            <Box>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontSize: '0.875rem' }}>
                Collez l'URL Firebase complète du fichier PDF déjà uploadé (CTVO, accusé d'enregistrement TPC, etc.)
              </Typography>
              <TextField
                autoFocus
                fullWidth
                className="inputElement"
                label="URL Firebase du fichier PDF"
                value={manualReceiptFirebaseUrl}
                onChange={(e) => setManualReceiptFirebaseUrl(e.target.value)}
                placeholder="https://firebasestorage.googleapis.com/..."
                disabled={isSubmittingManual}
                helperText="L'URL doit contenir 'firebasestorage.googleapis.com' et pointer vers un fichier PDF"
                error={
                  manualReceiptFirebaseUrl.length > 0 &&
                  (!manualReceiptFirebaseUrl.includes('firebasestorage.googleapis.com') ||
                    !manualReceiptFirebaseUrl.toLowerCase().includes('.pdf'))
                }
                margin="dense"
                multiline
                rows={3}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            className="secondaryDealActionButton"
            onClick={() => {
              setIsManualCTVOModalOpen(false);
              setManualReceiptFileName('');
              setManualReceiptFirebaseUrl('');
              setManualCTVOMethod('filename');
              setShouldInvoiceCTVO(true);
            }}
            disabled={isSubmittingManual}
          >
            Annuler
          </Button>
          <Button
            className="dealActionButton"
            variant="contained"
            onClick={handleManualCTVOSubmit}
            disabled={
              isSubmittingManual ||
              (manualCTVOMethod === 'filename' && !manualReceiptFileName.trim()) ||
              (manualCTVOMethod === 'url' && !manualReceiptFirebaseUrl.trim())
            }
          >
            {isSubmittingManual && <CircularProgress size={20} style={{ color: 'white', marginRight: 8 }} />}
            Valider
          </Button>
        </DialogActions>
      </Dialog>

      <ErrorMessageSnackbar
        open={validation.snackbarOpen}
        message={validation.snackbarMessage}
        handleClose={() => validation.setSnackbarOpen(false)}
        severity={validation.severity}
      />
    </>
  );
};
export default SubmitCTVO;
